import type {
  CancelStreamRequest,
  CreateStreamRequest,
  GetStreamListRequest,
  GetStreamListResponse,
  JoinTokenResponse,
  KickRequest,
  LiveCategory,
  LiveCounts,
  LiveSpeaker,
  LiveTopic,
  RawLiveCategory,
  RawLiveSpeaker,
  RawLiveTopic,
  RemindRequest,
  StageRequest,
  StageResponse,
  StartStreamResponse,
  Stream,
  StreamDetail
} from '@/models/interface/liveStream';
import goLiveStreamAxios, { GoLiveStreamError } from '@/utils/api/golivestream-axios';

/** 呼叫端自己處理錯誤時用，避免 instance 再跳一次全域提示。 */
const SILENT = { headers: { 'X-Silent-Error': 'true' } } as const;

/** 背景請求：不顯示全域 loading，失敗也不跳提示。 */
const SILENT_BACKGROUND = {
  headers: { disableGlobalLoading: true, 'X-Silent-Error': 'true' }
} as const;

/**
 * 不顯示全域 loading，但失敗照樣跳提示。
 *
 * 與 `SILENT_BACKGROUND` 的差別就在錯誤提示 —— 無限捲動載下一頁是使用者的動作，
 * 失敗要讓他知道；只是不該為了這個把整頁蓋掉。
 */
const NO_GLOBAL_LOADING = { headers: { disableGlobalLoading: true } } as const;

/** 把「查無資料」的 404 轉成 null。其他錯誤照樣往上拋。 */
async function nullOn404<T>(request: () => Promise<T>): Promise<T | null> {
  try {
    return await request();
  } catch (error) {
    if (error instanceof GoLiveStreamError && error.status === 404) return null;
    throw error;
  }
}

/**
 * 直播微服務（go-livestream）。
 *
 * 不繼承 `ApiFactory` —— 那支綁在共用的 axios instance 與 `{isSuccess, content}`
 * 信封上，也會把建構子的 `apiUrl` 當成所有路徑的前綴。這裡的方法直接回傳資料本身，
 * 失敗時丟出 `GoLiveStreamError`（呼叫端用 try/catch，不用檢查 `isSuccess`）。
 */
export class LiveStreamService {
  /* ---------------------------------------------------------------- 直播 */

  /**
   * 直播清單
   * @param options.silent 背景呼叫時傳 true：不顯示全域 loading、失敗也不跳提示。
   *                       版頭那顆「有沒有人在直播」的指示燈就是這樣用的，它不該讓整頁轉圈。
   * @param options.globalLoading 傳 false 只關掉全域 loading，錯誤提示照舊。
   *                              無限捲動載下一頁用 —— 整頁轉圈會把使用者正在看的內容蓋掉，
   *                              而且擋住捲動，等於每載一頁就打斷一次。
   * @remarks 清單有 30 秒快取，剛建立的直播可能要等一下才出現；查單筆詳細則一定是最新的。
   */
  async getStreams(
    params?: GetStreamListRequest,
    options: { silent?: boolean; globalLoading?: boolean } = {}
  ): Promise<GetStreamListResponse> {
    const { silent = false, globalLoading = true } = options;
    const { data } = await goLiveStreamAxios.get<GetStreamListResponse>('/streams', {
      params,
      ...(silent ? SILENT_BACKGROUND : globalLoading ? {} : NO_GLOBAL_LOADING)
    });
    return data;
  }

  /** 建立直播。需開播權限，否則 403。建立時不會開房也不會錄影，要另外呼叫開播。 */
  async createStream(payload: CreateStreamRequest): Promise<Stream> {
    const { data } = await goLiveStreamAxios.post<Stream>('/streams', payload);
    return data;
  }

  /**
   * 目前可加入、或時間上最接近的一場。
   * @remarks 沒有任何一場時後端回 404 —— 那是正常結果，這裡轉成 `null`，不跳錯誤提示。
   */
  async getNextStream(): Promise<StreamDetail | null> {
    return nullOn404(async () => {
      const { data } = await goLiveStreamAxios.get<StreamDetail>('/streams/next', SILENT);
      return data;
    });
  }

  /** 各場線上人數。直接向 LiveKit 查，不經資料庫，所以是即時值。 */
  async getLiveCounts(): Promise<LiveCounts> {
    const { data } = await goLiveStreamAxios.get<LiveCounts>(
      '/streams/live-counts',
      SILENT_BACKGROUND
    );
    return data;
  }

  /**
   * 我最近一場未關播的直播，給講師端「續播」用。
   * @remarks 沒有未關播的直播是**常態**（後端回 404），這裡轉成 `null`，
   *          否則每個講師一進直播頁都會看到一則錯誤提示。
   */
  async getMyLastStream(): Promise<Stream | null> {
    return nullOn404(async () => {
      const { data } = await goLiveStreamAxios.get<Stream>('/streams/mine/last', SILENT);
      return data;
    });
  }

  /**
   * 直播詳細。不吃清單快取。
   * @param silent 呼叫端自己處理錯誤時傳 true，避免和自己的提示重複跳兩則訊息
   */
  async getStream(id: string, silent = false): Promise<StreamDetail> {
    const { data } = await goLiveStreamAxios.get<StreamDetail>(
      `/streams/${id}`,
      silent ? SILENT : undefined
    );
    return data;
  }

  /**
   * 開播。限本場講師。
   * @remarks 整段是冪等的：連按兩次或斷線重連後再打一次，都不會產生第二場直播或第二份錄影。
   */
  async startStream(id: string): Promise<StartStreamResponse> {
    const { data } = await goLiveStreamAxios.post<StartStreamResponse>(`/streams/${id}/start`);
    return data;
  }

  /** 關播。限本場講師，已經關過的再打一次也成功（冪等）。 */
  async closeStream(id: string): Promise<void> {
    await goLiveStreamAxios.post(`/streams/${id}/close`);
  }

  /** 取消尚未開播的直播。限本場講師。 */
  async cancelStream(id: string, payload?: CancelStreamRequest): Promise<void> {
    await goLiveStreamAxios.post(`/streams/${id}/cancel`, payload ?? {});
  }

  /**
   * 踢出並封鎖某位觀眾。限本場講師，不能踢自己。
   * @remarks 被踢的人會同時被封鎖再入，之後呼叫 token 端點會拿到 403。
   */
  async kick(id: string, payload: KickRequest): Promise<void> {
    await goLiveStreamAxios.post(`/streams/${id}/kick`, payload);
  }

  /** 設定或取消開播提醒。設了提醒的人會在這場「首次」開播時收到推播，重開播不再推。 */
  async setRemind(id: string, payload: RemindRequest): Promise<void> {
    await goLiveStreamAxios.put(`/streams/${id}/remind`, payload);
  }

  /* ---------------------------------------------------------------- 進房 */

  /**
   * 取得 LiveKit 進房 token。
   * @remarks
   * - 角色由後端決定，前端不可指定。
   * - token 有效期短（預設 15 分鐘），但 LiveKit 會自動為已連線的人續期，
   *   所以只在準備進房時取一次即可，**不需要自己排程更新**。
   * - 被本場封鎖過的人回 403。
   * - **靜音**：403 要跳的是「您已被踢出房間」的彈窗，不是全域錯誤 toast，
   *   由 `useLiveKitRoom` 的 `join()` 依 status 分流。
   */
  async getJoinToken(id: string): Promise<JoinTokenResponse> {
    const { data } = await goLiveStreamAxios.post<JoinTokenResponse>(
      `/streams/${id}/token`,
      undefined,
      SILENT
    );
    return data;
  }

  /* ------------------------------------------------------------ 觀眾上台 */

  /**
   * 查目前誰在台上。
   * @remarks 已經在房內的話不必打這支 —— 參與者 metadata 會即時反映同一份資訊。
   *          這支是給「剛進房還沒收到事件」或「重連後要補畫面」用的。
   */
  async getStage(id: string): Promise<StageResponse> {
    const { data } = await goLiveStreamAxios.get<StageResponse>(`/streams/${id}/stage`);
    return data;
  }

  /**
   * 請一位觀眾上台分享。限本場講師。
   * @remarks 對象必須此刻就在房間裡，否則 400；台上人數已滿回 409。
   *          回傳更新後的名單，不必再打一次 GET。
   */
  async grantStage(id: string, payload: StageRequest): Promise<StageResponse> {
    const { data } = await goLiveStreamAxios.post<StageResponse>(`/streams/${id}/stage`, payload);
    return data;
  }

  /** 請一位觀眾下台。講師可請任何人下台，觀眾帶自己的會員編號可請自己下台。冪等。 */
  async revokeStage(id: string, memberId: number): Promise<void> {
    await goLiveStreamAxios.delete(`/streams/${id}/stage/${memberId}`);
  }

  /* ------------------------------------------------------------ 清單資料 */

  /**
   * 直播分類。
   * @remarks 線路上是 Pascal 命名，這裡正規化成 camelCase 再交出去。
   */
  async getCategories(): Promise<LiveCategory[]> {
    const { data } = await goLiveStreamAxios.get<RawLiveCategory[]>('/categories');
    return data.map((item) => ({
      id: item.ID,
      chLabel: item.ChLabel,
      enLabel: item.EnLabel,
      sortOrder: item.SortOrder
    }));
  }

  /**
   * 主題建議清單。`categoryId` 不帶或 0 表示不限分類。
   * @remarks 線路上是 Pascal 命名，這裡正規化成 camelCase 再交出去。
   */
  async getTopics(categoryId?: number): Promise<LiveTopic[]> {
    const { data } = await goLiveStreamAxios.get<RawLiveTopic[]>('/topics', {
      params: categoryId ? { categoryId } : undefined
    });
    return data.map((item) => ({
      id: item.ID,
      category: item.CategoryID,
      chLabel: item.ChLabel,
      enLabel: item.EnLabel,
      outline: item.Outline
    }));
  }

  /**
   * 可開播的講師清單。
   * @remarks 線路上是 Pascal 命名，這裡正規化成 camelCase 再交出去。
   */
  async getSpeakers(): Promise<LiveSpeaker[]> {
    const { data } = await goLiveStreamAxios.get<RawLiveSpeaker[]>('/speakers');
    return data.map((item) => ({
      speakerId: item.MemberID,
      speakerName: item.Nickname
    }));
  }
}

/**
 * 是否已經開播。
 *
 * `realStartTime` 在尚未開播時是**整個 key 不存在**，不是 null ——
 * 用 `stream.realStartTime === null` 或 `!stream.realStartTime` 判斷都會出錯。
 */
export function isStreamStarted(stream: Stream): boolean {
  return 'realStartTime' in stream;
}

/** 是否已經關播。同上，`endTime` 尚未關播時整個 key 不存在。 */
export function isStreamEnded(stream: Stream): boolean {
  return 'endTime' in stream;
}
